/**
 * View Selector Dropdown - Matches clinicui design with Material-UI
 * Allows users to select, save, and manage table views
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Box,
  Paper,
  Typography,
  Divider,
  ListItem,
  List,
} from '@mui/material';

export interface SavedView {
  id: string;
  name: string;
  type: 'private' | 'public';
  list_name?: string;
  is_default?: boolean;
  description?: string;
  data?: {
    columnOrder?: string[];
    visibleColumns?: string[];
    columnWidths?: Record<string, number>;
    sortBy?: string | null;
    sortOrder?: 'asc' | 'desc';
    [key: string]: any;
  };
  created_at?: string;
  updated_at?: string;
}

interface ViewSelectorProps {
  views: SavedView[];
  currentViewId: string | null;
  onSelectView: (viewId: string) => void;
  onSaveView: () => void;
  onSaveAsView: () => void;
  onDeleteView: (viewId: string) => void;
  onSetDefault?: (viewId: string) => void;
  isLoading?: boolean;
  isAdmin?: boolean;
}

const ViewSelector: React.FC<ViewSelectorProps> = ({
  views = [],
  currentViewId,
  onSelectView,
  onSaveView,
  onSaveAsView,
  onDeleteView,
  onSetDefault,
  isLoading = false,
  isAdmin = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const privateViews = views.filter(v => v && v.type === 'private');
  const publicViews = views.filter(v => v && v.type === 'public');
  const currentView = views.find(v => String(v.id) === String(currentViewId));
  const displayName = currentView?.name || 'Default View';

  return (
    <Box ref={dropdownRef} sx={{ position: 'relative', display: 'inline-block' }}>
      {/* Trigger Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        variant="outlined"
        size="small"
        sx={{
          minWidth: '160px',
          textTransform: 'none',
          fontSize: { xs: '0.75rem', md: '0.875rem' },
          px: { xs: 1, md: 2 },
          justifyContent: 'space-between',
          color: '#666',
          borderColor: '#ddd',
          '&:hover': {
            borderColor: '#999',
            backgroundColor: '#fafafa',
          },
        }}
      >
        <span style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'left',
          minWidth: 0,
        }}>
          {isLoading ? 'Loading...' : displayName}
        </span>
        <span style={{ fontSize: '0.65rem', marginLeft: '8px', flexShrink: 0 }}>▼</span>
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <Paper
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: '280px',
            maxHeight: '400px',
            overflowY: 'auto',
            zIndex: 1300,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          }}
        >
          {/* Save and Save As Section */}
          <Box sx={{ p: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              onClick={() => {
                onSaveView();
                setIsOpen(false);
              }}
              sx={{
                backgroundColor: '#2196F3',
                textTransform: 'none',
                fontSize: '0.875rem',
                py: 1,
                '&:hover': {
                  backgroundColor: '#1976D2',
                },
              }}
            >
              💾 Save
            </Button>
            <Button
              fullWidth
              variant="contained"
              size="small"
              onClick={() => {
                onSaveAsView();
                setIsOpen(false);
              }}
              sx={{
                backgroundColor: '#4CAF50',
                textTransform: 'none',
                fontSize: '0.875rem',
                py: 1,
                '&:hover': {
                  backgroundColor: '#45a049',
                },
              }}
            >
              ➕ Save As
            </Button>
          </Box>

          <Divider />

          {/* My Views Section */}
          {privateViews.length > 0 && (
            <>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  color: '#999',
                  textTransform: 'uppercase',
                  fontSize: '0.65rem',
                  letterSpacing: 0.5,
                }}
              >
                My Views
              </Typography>
              <List sx={{ py: 0 }}>
                {privateViews.map(view => (
                  <ViewItem
                    key={view.id}
                    view={view}
                    isSelected={String(view.id) === String(currentViewId)}
                    onSelect={() => {
                      onSelectView(view.id);
                      setIsOpen(false);
                    }}
                    onDelete={onDeleteView}
                    onSetDefault={onSetDefault}
                  />
                ))}
              </List>
            </>
          )}

          {/* Public Views Section */}
          {publicViews.length > 0 && (
            <>
              {privateViews.length > 0 && <Divider />}
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  color: '#999',
                  textTransform: 'uppercase',
                  fontSize: '0.65rem',
                  letterSpacing: 0.5,
                }}
              >
                Public Views
              </Typography>
              <List sx={{ py: 0 }}>
                {publicViews.map(view => (
                  <ViewItem
                    key={view.id}
                    view={view}
                    isSelected={String(view.id) === String(currentViewId)}
                    onSelect={() => {
                      onSelectView(view.id);
                      setIsOpen(false);
                    }}
                    onDelete={isAdmin ? onDeleteView : undefined}
                    onSetDefault={isAdmin ? onSetDefault : undefined}
                    isPublic
                  />
                ))}
              </List>
            </>
          )}

          {/* Empty State */}
          {privateViews.length === 0 && publicViews.length === 0 && (
            <Box sx={{ py: 4, px: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                No saved views yet.
              </Typography>
              <Typography variant="caption" sx={{ color: '#ccc' }}>
                Save your first view to get started!
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

interface ViewItemProps {
  view: SavedView;
  isSelected: boolean;
  onSelect: () => void;
  onDelete?: (viewId: string) => void;
  onSetDefault?: (viewId: string) => void;
  isPublic?: boolean;
}

const ViewItem: React.FC<ViewItemProps> = ({
  view,
  isSelected,
  onSelect,
  onDelete,
  isPublic = false,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showDeleteConfirm) {
      onDelete?.(view.id);
      setShowDeleteConfirm(false);
    } else {
      setShowDeleteConfirm(true);
      setTimeout(() => setShowDeleteConfirm(false), 3000);
    }
  };

  return (
    <ListItem
      onClick={onSelect}
      sx={{
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.2s',
        borderLeft: isSelected ? '3px solid #2196F3' : '3px solid transparent',
        pl: isSelected ? 'calc(2px + 16px)' : 2,
        backgroundColor: isSelected ? '#E3F2FD' : 'white',
        '&:hover': {
          backgroundColor: isSelected ? '#E3F2FD' : '#FAFAFA',
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: isSelected ? 600 : 500,
            color: '#333',
          }}
        >
          {isSelected && '✓ '}
          {view.name}
          {view.is_default && (
            <span style={{ marginLeft: '6px', fontSize: '0.875rem' }}>⭐</span>
          )}
          {isPublic && !view.is_default && (
            <span style={{ marginLeft: '6px', fontSize: '0.75rem', color: '#999' }}>🌐</span>
          )}
        </Typography>
        {view.description && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.25,
              color: '#999',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {view.description}
          </Typography>
        )}
      </Box>

      {onDelete && (
        <Button
          onClick={handleDelete}
          size="small"
          variant={showDeleteConfirm ? 'outlined' : 'text'}
          color={showDeleteConfirm ? 'error' : 'inherit'}
          sx={{
            ml: 1,
            minWidth: 'auto',
            px: 1,
            py: 0.5,
            fontSize: '0.75rem',
            flexShrink: 0,
          }}
        >
          {showDeleteConfirm ? '✓ Delete?' : '×'}
        </Button>
      )}
    </ListItem>
  );
};

export default ViewSelector;
